import { getOrderedKpiDefinitions, matchesKpiDefinition } from "../kpiConfiguration";
import { toFiniteNumber } from "./kpiFormatters";

function getEndpointPayload(data) {
  if (data?.reportData?.businessKeyFigures) {
    return data;
  }

  // Backward compatibility for state containing reportData only.
  if (data?.businessKeyFigures) {
    return {
      requestId: null,
      context: {},
      company: null,
      reportData: data,
    };
  }

  return {
    requestId: null,
    context: {},
    company: null,
    reportData: {
      businessKeyFigures: [],
    },
  };
}

function getRequestedYears(payload) {
  const filterYears = payload?.context?.filters?.surveyYears;

  if (Array.isArray(filterYears) && filterYears.length > 0) {
    return filterYears.map(Number).filter(Number.isFinite);
  }

  const windowYears = payload?.context?.dataWindow?.years;

  if (Array.isArray(windowYears) && windowYears.length > 0) {
    return windowYears.map(Number).filter(Number.isFinite);
  }

  return [];
}

export function getSelectedBusinessKeyFigures(data) {
  if (Array.isArray(data)) {
    return {
      period: null,
      keyFigures: data,
    };
  }

  const payload = getEndpointPayload(data);
  const periods = payload?.reportData?.businessKeyFigures?.filter((group) => Array.isArray(group?.keyFigures)) || [];

  if (periods.length === 0) {
    return {
      period: null,
      keyFigures: [],
    };
  }

  const requestedYears = getRequestedYears(payload);

  const explicitlySelected = periods
    .filter((group) => requestedYears.includes(Number(group.period)))
    .sort((a, b) => Number(b.period) - Number(a.period))[0];

  if (explicitlySelected) return explicitlySelected;
  if (requestedYears.length) return { period: null, keyFigures: [] };

  /*
   * Empty dataWindow.years does not mean there is no report.
   * The supplied endpoint contains a 2025 report, so use the newest
   * returned period when no explicit period was submitted.
   */
  return periods.reduce((newest, current) => (Number(current.period) > Number(newest.period) ? current : newest));
}

function readMean(keyFigure) {
  return toFiniteNumber(keyFigure?.mean ?? keyFigure?.metrics?.mean);
}

function readStandardDeviation(keyFigure) {
  return toFiniteNumber(
    keyFigure?.sd ??
      keyFigure?.standardDeviation ??
      keyFigure?.stddev ??
      keyFigure?.stdDev ??
      keyFigure?.metrics?.sd ??
      keyFigure?.metrics?.standardDeviation ??
      keyFigure?.metrics?.stddev ??
      keyFigure?.metrics?.stdDev
  );
}

function readRespondentValue(keyFigure) {
  return toFiniteNumber(
    keyFigure?.respondentValue ?? keyFigure?.companyValue ?? keyFigure?.metrics?.respondentValue ?? keyFigure?.metrics?.companyValue
  );
}

export function getKeyFiguresCurrency(data) {
  return getEndpointPayload(data)?.context?.unit || "CHF";
}

export function getKeyFiguresPageQuality(kpis) {
  const qualities = kpis
    .map((item) => item.quality)
    .filter((quality) => quality && toFiniteNumber(quality.count) !== null && toFiniteNumber(quality.coefficientOfVariation) !== null);

  if (qualities.length === 0) return null;

  const worstQuality = qualities.reduce((worst, current) =>
    Math.abs(current.coefficientOfVariation) > Math.abs(worst.coefficientOfVariation) ? current : worst
  );

  /*
   * Use the smallest count if individual KPI populations differ. It is
   * safer than claiming that all displayed KPIs have the first KPI's count.
   */
  const count = Math.min(...qualities.map((quality) => Number(quality.count)));

  return {
    count,
    coefficientOfVariation: worstQuality.coefficientOfVariation,
    cvBand: worstQuality.cvBand,
  };
}

export function parseKpiScoreCardData(data, translate) {
  const selectedGroup = getSelectedBusinessKeyFigures(data);
  const keyFigures = selectedGroup.keyFigures || [];
  const definitions = getOrderedKpiDefinitions(translate);

  return definitions
    .map((definition) => {
      const raw = keyFigures.find((keyFigure) => matchesKpiDefinition(keyFigure, definition));
      if (!raw) return null;
      const mean = readMean(raw);
      const respondentValue = readRespondentValue(raw);

      return {
        ...definition,
        period: selectedGroup.period,
        figureName: raw.figureName ?? raw.kpiId ?? definition.id,
        mean,
        sd: readStandardDeviation(raw),
        respondentValue,
        quality: raw?.metrics?.quality ?? raw?.quality ?? null,
      };
    })
    .filter(Boolean);
}
